/**
 * What the field picker lists for one Waterlily field, and in what order.
 *
 * An org has a hundred or more fields and most cannot hold the value being asked for, so the list
 * starts short: the candidates the matcher proposed, then the fields whose type usually holds the
 * value, grouped by the record they are read from. Fields of a type that cannot hold it, or only
 * sometimes does (a number typed into a text field), are left out until the advisor asks for
 * everything, with a count so they know something is hidden.
 *
 * A candidate is listed once, under "Suggested", not again in its own group. The field currently
 * chosen is always listed, even when its type would hide it, so the picker never shows a selection
 * it does not offer.
 */
import type { Prospecting } from '@client/api/types';

export interface PickerSection {
  /** `null` for the contact's own fields, otherwise the related record's name ("Account"). */
  group: string | null;
  fields: Prospecting.DiscoveredField[];
}

export interface PickerOptions {
  suggested: { field: Prospecting.DiscoveredField; candidate: Prospecting.FieldMatchCandidate }[];
  sections: PickerSection[];
  hiddenCount: number;
}

interface PickerInput {
  fields: Prospecting.DiscoveredField[];
  candidates: Prospecting.FieldMatchCandidate[];
  /** CRM field types that do not usually hold this Waterlily field. */
  unsuitableTypes: string[];
  selected: string | null;
  showAll: boolean;
}

export function pickerOptions({ fields, candidates, unsuitableTypes, selected, showAll }: PickerInput): PickerOptions {
  const byName = new Map(fields.map(field => [field.name, field]));
  const suggested = candidates.flatMap(candidate => {
    const field = byName.get(candidate.sourceField);
    return field ? [{ field, candidate }] : [];
  });
  const suggestedNames = new Set(suggested.map(entry => entry.field.name));
  const unsuitable = new Set(unsuitableTypes);

  const rest = fields.filter(field => !suggestedNames.has(field.name));
  const visible = rest.filter(field => showAll || field.name === selected || !unsuitable.has(field.type));

  const groups = [...new Set(visible.map(field => field.group ?? null))].sort(
    (a, b) => Number(a !== null) - Number(b !== null),
  );
  const sections = groups.map(group => ({
    group,
    fields: visible.filter(field => (field.group ?? null) === group),
  }));

  return { suggested, sections, hiddenCount: rest.length - visible.length };
}
